import React, { useState, useEffect } from "react";
import { X, Check, CheckSquare, Square, Download, Film, Layers, Clapperboard, ChevronLeft, ListOrdered } from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";

// 三种导入来源的配置（分镜 / 小故事板 / 导演故事台）
const SOURCE_CONFIG = {
  storyboard: { title: "导入分镜", Icon: Film, emptyText: "当前集暂无分镜数据", unit: "个分镜" },
  mini: { title: "导入小故事板", Icon: Layers, emptyText: "当前集暂无小故事板数据", unit: "个分段" },
  board: { title: "导入导演故事台", Icon: Clapperboard, emptyText: "当前集暂无导演故事台数据", unit: "张故事板" },
};

// 各来源的数据加载（返回原始响应）
const fetchers = {
  storyboard: (projectId, episodeId) =>
    authenticatedFetch(`${API_HOST}/api/storyboard/load?episodeId=${episodeId}`)
      .then((res) => res?.data || res || []),
  mini: (projectId, episodeId) =>
    authenticatedFetch(`${API_HOST}/api/episode-mini-storyboard/list?projectId=${projectId}&episodeId=${episodeId}`),
  board: (projectId, episodeId) =>
    authenticatedFetch(`${API_HOST}/api/episode-storyboard-board/list?projectId=${projectId}&episodeId=${episodeId}`),
};

// 各来源 → 统一 shot 结构（与 NodeCanvas.handleImportShots 的现有映射对齐：
// id / shot_number / shot / image_prompt / video_prompt / content / generated_image / generated_video）
// duration_seconds 为可选附加字段，导入时透传到节点 data.durationSeconds
const normalizers = {
  // 分镜：后端结构已与目标一致，原样透传
  storyboard: (res) => (Array.isArray(res) ? res : []),
  // 小故事板：一个分段（segment）映射为一个节点；分段内 shots 的 visual/text 合并为画面提示词/内容
  mini: (res) =>
    (Array.isArray(res?.segments) ? res.segments : []).map((seg) => {
      const segShots = Array.isArray(seg?.shots) ? seg.shots : [];
      return {
        id: `mini-${seg?.id ?? seg?.segmentIndex}`,
        shot_number: seg?.segmentIndex,
        shot: seg?.title || null,
        image_prompt: segShots.map((s) => s?.visual).filter(Boolean).join("\n") || null,
        video_prompt: typeof seg?.seedancePrompt === "string" ? seg.seedancePrompt : null,
        content:
          seg?.narrationText ||
          seg?.sourceText ||
          segShots.map((s) => s?.text).filter(Boolean).join("\n") ||
          null,
        generated_image: seg?.thumbnailUrl || null,
        generated_video: seg?.videoUrl || null,
        duration_seconds: seg?.durationSeconds ?? null,
      };
    }),
  // 导演故事台：一张故事板（board）映射为一个节点；无独立视频提示词，内容取分段文本
  board: (res) =>
    (Array.isArray(res?.boards) ? res.boards : []).map((b) => ({
      id: `board-${b?.id ?? b?.index}`,
      shot_number: b?.index,
      shot: b?.title || null,
      image_prompt: b?.prompt || null,
      video_prompt: null,
      content: b?.segmentText || b?.sourceText || b?.spokenText || null,
      generated_image: b?.imageUrl || null,
      generated_video: null,
      duration_seconds: b?.durationSeconds ?? null,
    })),
};

// 集数显示名：第N集 + 标题
const episodeLabel = (ep) => {
  const order = Number(ep?.sortOrder || ep?.episodeNumber || 0);
  const title = String(ep?.title || "").trim();
  if (!order) return title || `集数 #${ep?.id}`;
  return title ? `第${order}集 ${title}` : `第${order}集`;
};

const StoryboardImportPanel = ({ projectId, episodeId, source = "storyboard", existingShotIds, onImport, onClose, exiting }) => {
  const config = SOURCE_CONFIG[source] || SOURCE_CONFIG.storyboard;
  const { Icon } = config;

  // episodeId 由外部固定时（单集工作台画布 tab）跳过选集步骤
  const fixedEpisodeId = episodeId || null;

  const [episodes, setEpisodes] = useState([]);
  const [episodesLoading, setEpisodesLoading] = useState(false);
  const [activeEpisodeId, setActiveEpisodeId] = useState(fixedEpisodeId);
  const [shots, setShots] = useState([]);
  const [loading, setLoading] = useState(!!fixedEpisodeId);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [importing, setImporting] = useState(false);

  // 选集步骤：进入面板且未固定集数时，拉取项目分集列表
  useEffect(() => {
    if (fixedEpisodeId || !projectId) return;
    const loadEpisodes = async () => {
      try {
        setEpisodesLoading(true);
        const res = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`);
        const list = Array.isArray(res) ? [...res] : [];
        list.sort((a, b) => {
          const orderDiff = Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0);
          return orderDiff || Number(a?.id || 0) - Number(b?.id || 0);
        });
        setEpisodes(list);
      } catch (error) {
        console.error("加载分集列表失败:", error);
      } finally {
        setEpisodesLoading(false);
      }
    };
    loadEpisodes();
  }, [projectId, fixedEpisodeId]);

  // 按来源加载数据（选定集数后）
  useEffect(() => {
    if (!activeEpisodeId) return;
    const load = async () => {
      try {
        setLoading(true);
        setSelectedIds(new Set());
        const res = await fetchers[source](projectId, activeEpisodeId);
        setShots((normalizers[source] || normalizers.storyboard)(res));
      } catch (error) {
        console.error("加载导入数据失败:", error);
        setShots([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeEpisodeId, source, projectId]);

  const isImported = (shot) => existingShotIds?.has(shot.id?.toString());

  const toggleSelect = (shotId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(shotId)) next.delete(shotId);
      else next.add(shotId);
      return next;
    });
  };

  const selectAll = () => {
    const available = shots.filter(s => !isImported(s)).map(s => s.id);
    setSelectedIds(new Set(available));
  };

  const deselectAll = () => setSelectedIds(new Set());

  const handleImport = async (shotsToImport) => {
    setImporting(true);
    try {
      await onImport(shotsToImport);
      onClose();
    } catch (error) {
      console.error("导入失败:", error);
    } finally {
      setImporting(false);
    }
  };

  const importSelected = () => {
    const selected = shots.filter(s => selectedIds.has(s.id));
    if (selected.length > 0) handleImport(selected);
  };

  const importAll = () => {
    const available = shots.filter(s => !isImported(s));
    if (available.length > 0) handleImport(available);
  };

  const availableCount = shots.filter(s => !isImported(s)).length;
  const showEpisodePicker = !fixedEpisodeId && !activeEpisodeId;
  const activeEpisode = episodes.find(ep => String(ep.id) === String(activeEpisodeId));

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
    >
      {/* 背景遮罩 */}
      <div className={`absolute inset-0 bg-[var(--overlay-light)] ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`} />

      {/* 侧边面板 */}
      <div
        className={`relative w-[420px] h-full flex flex-col ${exiting ? "animate-out slide-out-to-right-4 duration-150" : "animate-in slide-in-from-right duration-200"}`}
        style={{ backgroundColor: 'var(--color-bg-secondary)', borderLeft: '1px solid var(--color-border-primary)', boxShadow: 'var(--shadow-modal)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* 头部 */}
        <div
          className="px-4 py-3 flex items-center justify-between border-b"
          style={{ borderColor: 'var(--color-border-primary)' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            {!fixedEpisodeId && activeEpisodeId && (
              <button
                onClick={() => { setActiveEpisodeId(null); setShots([]); setSelectedIds(new Set()); }}
                className="p-1 rounded-md transition-all duration-200 flex-shrink-0"
                style={{ color: 'var(--color-text-muted)' }}
                title="返回选集"
              >
                <ChevronLeft size={16} />
              </button>
            )}
            <Icon size={18} className="flex-shrink-0" style={{ color: 'var(--color-accent-primary)' }} />
            <span className="text-sm font-bold truncate" style={{ color: 'var(--color-text-primary)' }}>
              {config.title}
            </span>
            {activeEpisode && (
              <span className="text-label truncate">{episodeLabel(activeEpisode)}</span>
            )}
            {!showEpisodePicker && (
              <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 font-mono-num" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
                {shots.length} {config.unit}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-all duration-200 flex-shrink-0"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <X size={18} />
          </button>
        </div>

        {showEpisodePicker ? (
          /* 选集步骤 */
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-tertiary)' }}>
              <span className="text-label">选择要导入的集数</span>
            </div>
            {episodesLoading ? (
              <div className="flex items-center justify-center h-40">
                <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>加载分集列表...</span>
              </div>
            ) : episodes.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 gap-2">
                <ListOrdered size={32} style={{ color: 'var(--color-text-muted)' }} />
                <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>当前项目暂无分集</span>
              </div>
            ) : (
              episodes.map(ep => (
                <div
                  key={ep.id}
                  className="px-4 py-3 flex items-center gap-3 border-b transition-all duration-200 cursor-pointer"
                  style={{ borderColor: 'var(--color-border-primary)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  onClick={() => setActiveEpisodeId(ep.id)}
                >
                  <span className="text-xs font-bold font-mono-num flex-shrink-0" style={{ color: 'var(--color-accent-primary)' }}>
                    第{Number(ep?.sortOrder || ep?.episodeNumber || 0) || '-'}集
                  </span>
                  <span className="text-xs truncate" style={{ color: 'var(--color-text-secondary)' }}>
                    {String(ep?.title || '').trim() || '未命名集数'}
                  </span>
                </div>
              ))
            )}
          </div>
        ) : (
          <>
            {/* 操作栏 */}
            <div
              className="px-4 py-2 flex items-center justify-between border-b"
              style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-tertiary)' }}
            >
              <div className="flex items-center gap-2">
                <button
                  onClick={selectedIds.size > 0 ? deselectAll : selectAll}
                  className="text-xs px-2 py-1 rounded transition-all duration-200"
                  style={{ color: 'var(--color-blue-primary)' }}
                >
                  {selectedIds.size > 0 ? '取消全选' : '全选'}
                </button>
                {selectedIds.size > 0 && (
                  <span className="text-xs font-mono-num" style={{ color: 'var(--color-text-muted)' }}>
                    已选 {selectedIds.size} 个
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selectedIds.size > 0 && (
                  <button
                    onClick={importSelected}
                    disabled={importing}
                    className="text-xs px-3 py-1.5 rounded-lg font-bold transition-all duration-200 flex items-center gap-1"
                    style={{ backgroundColor: 'var(--color-blue-primary)', color: 'white', opacity: importing ? 0.6 : 1 }}
                  >
                    <Download size={12} />
                    导入选中 ({selectedIds.size})
                  </button>
                )}
                <button
                  onClick={importAll}
                  disabled={importing || availableCount === 0}
                  className="text-xs px-3 py-1.5 rounded-lg font-bold transition-all duration-200 flex items-center gap-1"
                  style={{
                    backgroundColor: 'var(--color-accent-primary)',
                    color: 'white',
                    opacity: (importing || availableCount === 0) ? 0.6 : 1,
                  }}
                >
                  <Download size={12} />
                  导入全部 ({availableCount})
                </button>
              </div>
            </div>

            {/* 数据列表 */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>加载中...</span>
                </div>
              ) : shots.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 gap-2">
                  <Icon size={32} style={{ color: 'var(--color-text-muted)' }} />
                  <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{config.emptyText}</span>
                </div>
              ) : (
                shots.map(shot => {
                  const imported = isImported(shot);
                  const selected = selectedIds.has(shot.id);

                  return (
                    <div
                      key={shot.id}
                      className="px-4 py-3 flex items-start gap-3 border-b transition-all duration-200 cursor-pointer"
                      style={{
                        borderColor: 'var(--color-border-primary)',
                        backgroundColor: selected ? 'color-mix(in srgb, var(--color-blue-primary) 6%, transparent)' : 'transparent',
                        opacity: imported ? 0.5 : 1,
                      }}
                      onClick={() => !imported && toggleSelect(shot.id)}
                    >
                      {/* 勾选框 */}
                      <div className="pt-1 flex-shrink-0">
                        {imported ? (
                          <Check size={16} style={{ color: 'var(--color-text-muted)' }} />
                        ) : selected ? (
                          <CheckSquare size={16} style={{ color: 'var(--color-blue-primary)' }} />
                        ) : (
                          <Square size={16} style={{ color: 'var(--color-text-muted)' }} />
                        )}
                      </div>

                      {/* 缩略图 */}
                      <div
                        className="w-20 h-14 flex-shrink-0 rounded overflow-hidden flex items-center justify-center"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                      >
                        {shot.generated_image ? (
                          <img src={shot.generated_image} alt="" className="w-full h-full object-contain" />
                        ) : (
                          <Icon size={16} style={{ color: 'var(--color-text-muted)' }} />
                        )}
                      </div>

                      {/* 信息 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold font-mono-num" style={{ color: 'var(--color-accent-primary)' }}>
                            #{shot.shot_number}
                          </span>
                          {shot.duration_seconds != null && (
                            <span className="text-2xs font-mono-num" style={{ color: 'var(--color-text-muted)' }}>
                              {shot.duration_seconds}s
                            </span>
                          )}
                          {imported && (
                            <span className="text-2xs px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
                              已导入
                            </span>
                          )}
                        </div>
                        <div className="text-2xs mt-0.5 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                          {shot.image_prompt || shot.content || '无描述'}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StoryboardImportPanel;
